import { CheckCheck, LayoutGrid, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { NavLink, useParams, useLocation, useNavigate } from 'react-router-dom';
import { haptic } from '../utils/haptics';
import { ouvrirDecider } from './decider/Decider';
import { CreerSheet, useCreerSheet } from './creer/CreerSheet';
import { useNavModules } from './mobile/useNavModules';
import { MODULES_360 } from './accueil/Sidebar360';
import { IgSheet } from './ig/IgSheet';
import { Icone360 } from './kit/Icones360';
import { useAcces, moduleVisible } from '../hooks/useAcces';
import { usePlus360 } from '../hooks/usePlus360';
import './bottom-nav.css';
import './kit/nav-360.css';
import './lot68/nav71.css';
import { ouvrirCredits } from './accueil/CreditsSheet';
import { styleModule69, visage69, AGENT_MODULE69 } from './lot68/Sidebar69';
import { sqUrl } from './lot67/Paquet67';
import { useMissions71, missionCourante71 } from './lot68/missions71';

/* LOT 71 · P12 · barre du bas : Accueil · Décider · Créer · Modules · Missions ; P13 · la fenêtre Modules aux couleurs des agents */
const M = Object.fromEntries(MODULES_360.map((m) => [m.key, m]));
const CACHES = ['finance', 'stock', 'achats', 'recettes', 'haccp', 'equipe', 'basecamp', 'recrutement', 'communication', 'reservations'].filter((k) => M[k]);

const Anneau = ({ p, r = 19 }) => { const C = 2 * Math.PI * r; return <svg viewBox={`0 0 ${2 * r + 4} ${2 * r + 4}`} aria-hidden="true"><circle cx={r + 2} cy={r + 2} r={r} className="f" /><circle cx={r + 2} cy={r + 2} r={r} className="a" strokeDasharray={`${C * p} ${C}`} /></svg>; };

const MissionGrande71 = ({ mc, onOuvrir }) => <button type="button" className="b71-mis" onClick={onOuvrir} data-testid="nav-plus-missions">
  {sqUrl(mc.m) && <img src={sqUrl(mc.m)} alt="" />}
  <small>★ Missions · {mc.ec.length ? `${mc.ec.length} en cours` : 'à lancer'}{mc.n_conseillees ? ` · ${mc.n_conseillees} conseillée${mc.n_conseillees > 1 ? 's' : ''}` : ''}</small>
  <b>{mc.m.titre}<span className="dot">.</span></b>
  <span className="b71-mis-et">{(mc.et.length ? mc.et : [0, 0, 0]).slice(0, 6).map((e, i) => <i key={i} className={e && e.fait ? 'on' : ''} />)}</span>
</button>;

const FeuilleModules = ({ base, d, onClose }) => {
  const navigate = useNavigate();
  const acces = useAcces();
  const { locked } = usePlus360();
  const mc = missionCourante71(useMissions71());
  const n = (k) => d?.modules?.[k] || 0;
  const mods = CACHES.filter((k) => moduleVisible(acces, k === 'reservations' ? 'communication' : k));
  const total = mods.reduce((s, k) => s + n(k), 0);
  const go = (to) => { onClose(); navigate(`${base}${to}`); };
  return <IgSheet open onClose={onClose} title={<>Modules<span style={{ color: 'var(--orange)' }}>.</span>{total > 0 && <small className="b71-tot" data-testid="nav-plus-total">{total} à traiter</small>}</>} testId="nav-plus-feuille">
    <div className="b71-feuille" data-keepicon="" data-garde-couleur="">
      {mc && <MissionGrande71 mc={mc} onOuvrir={() => go(mc.m.en_cours && mc.m.plan_id ? `/dashboard?onglet=plans&mission=${mc.m.plan_id}` : '/missions')} />}
      <div className="b71-tuiles">{mods.map((k) => { const verrou = M[k].plusKey && locked; const a = AGENT_MODULE69[k] || 'sabril';
        return <button key={k} type="button" className="b71-t" style={styleModule69(k)} onClick={() => go(verrou ? '/dashboard?feuille=abonnement' : M[k].path)} data-testid={`nav-plus-${k}`}>
          <span className="b71-t-i"><Icone360 k={M[k].ico} size={20} /></span><img className="b71-t-v" src={visage69(a)} alt="" />
          <b className={M[k].label.length > 11 ? 'is-long' : ''}>{M[k].label}<span className="dot">.</span></b>
          {verrou ? <small>inclus dans 360+</small> : n(k) ? <em data-testid={`nav-plus-${k}-n`}>{n(k)} à traiter</em> : <small>tout est à jour</small>}
        </button>; })}</div>
      <div className="b71-bas">
        <button type="button" className="l71-b" onClick={() => { onClose(); ouvrirCredits(); }} data-testid="nav-plus-credits">★ Crédits</button>
        <button type="button" className="l71-b" onClick={() => go('/premier-jour')} data-testid="nav-plus-premier-jour">Premier jour</button>
        <button type="button" className="l71-b" onClick={() => go('/dashboard?feuille=reglages')} data-testid="nav-plus-reglages">Réglages</button>
      </div>
    </div>
  </IgSheet>;
};

export const BottomNav = () => {
  const { tenantSlug } = useParams();
  const base = tenantSlug ? `/${tenantSlug}` : '';
  const [open, setOpen] = useCreerSheet();
  const [plus, setPlus] = useState(false);
  const d = useNavModules();
  const mc = missionCourante71(useMissions71());
  const { pathname, search } = useLocation();
  const nMods = CACHES.reduce((s, k) => s + (d?.modules?.[k] || 0), 0);
  const rel = pathname.replace(base, '') || '/dashboard';
  const modK = CACHES.find((x) => M[x].match.some((p) => rel === p || rel.startsWith(`${p}/`)));
  const surPlus = rel === '/plus' || !!modK || /onglet=plans|feuille=reglages/.test(search);
  return (
    <>
      <nav className="bn bn71" aria-label="Navigation principale" data-testid="bottom-navigation">
        <NavLink to={`${base}/dashboard`} end className={() => `b71-i${rel === '/dashboard' ? ' on' : ''}`} style={styleModule69('accueil')} onClick={() => haptic.tap()} data-testid="nav-home">
          <span className="b71-x"><Icone360 k={M.accueil.ico} size={22} /></span><b>Accueil</b>
        </NavLink>
        <button type="button" className="b71-i is-dec" onClick={() => { haptic.tap(); ouvrirDecider(); }} data-keep-icon="" data-keepicon="" data-testid="nav-decider">
          <span className="b71-x"><CheckCheck size={18} strokeWidth={2.4} />{d?.a_decider > 0 && <i className="b71-n" aria-label="décisions en attente" data-testid="nav-decider-n">{d.a_decider}</i>}</span><b>Décider</b>
        </button>
        <button type="button" className="b71-i is-creer" onClick={() => { haptic.tap(); setOpen(true); }} data-keep-icon="" data-keepicon="" data-testid="nav-creer">
          <span className="b71-x"><Plus size={24} strokeWidth={2.6} /></span><b>Créer</b>
        </button>
        <button type="button" className={`b71-i is-mods${surPlus ? ' on' : ''}`} style={styleModule69(modK || 'accueil')} onClick={() => { haptic.tap(); setPlus(true); }} data-keepicon="" data-testid="nav-modules">
          <span className="b71-x"><LayoutGrid size={22} />{nMods > 0 && <i className="b71-n is-o" data-testid="nav-modules-n">{nMods}</i>}</span><b>Modules</b>
        </button>
        <NavLink to={`${base}/missions`} className={() => `b71-i is-mis${rel.startsWith('/missions') ? ' on' : ''}`} onClick={() => haptic.tap()} data-testid="nav-sabril">
          <span className="b71-x">{mc && sqUrl(mc.m) ? <img src={sqUrl(mc.m)} alt="" /> : <Icone360 k="missions" size={20} />}<Anneau p={mc?.p || 0} />{mc?.ec.length > 0 && <i className="b71-n is-v" data-testid="nav-missions-n">{mc.ec.length}</i>}</span><b>Missions</b>
        </NavLink>
      </nav>
      {plus && <FeuilleModules base={base} d={d} onClose={() => setPlus(false)} />}
      {!window.matchMedia('(min-width: 768px)').matches && <CreerSheet open={open} onClose={() => setOpen(false)} />}
    </>
  );
};
